/**
 * Presigned-upload contract.
 *
 * Carried forward from the spike helper so increment 2 (capture capsule) has a
 * settled contract to graduate onto. NOTHING IN THE WALKING SKELETON CALLS
 * THIS — no recording exists yet.
 *
 * Two things change versus the spike, both non-negotiable at the boundary:
 *   1. Uploads are scoped to a session that the caller has proven access to.
 *      The spike presigned a PUT for any path anyone asked about.
 *   2. Every presigned GET issuance must write an access_log row (ADR-0010 §4).
 *      That is a server obligation, recorded here so it is not rediscovered.
 */

import { z } from 'zod';

/**
 * Artifact kinds the capture pipeline produces (ADR-0004 full retention).
 *
 * D13 adds the snapshot pair, mirroring the video pair exactly: `snapshot` is
 * the stored pixels (PNG, patient-stream frame via the C4 sampler) and
 * `snapshot-pose` is its sidecar pose frame — skeleton composited at render,
 * never burned into the stored image (ADR-0006 change 3, same rule as video).
 * The same partNumber pairs them, like video-part/interpretation-stream.
 */
export const ARTIFACT_KINDS = [
  'video-part',
  'interpretation-stream',
  'snapshot',
  'snapshot-pose',
  'consent-document',
] as const;
export type ArtifactKind = (typeof ARTIFACT_KINDS)[number];

/**
 * Kinds that are SESSION capture (C8 recording under consent). The one
 * non-member, `consent-document`, is episode-scoped consent EVIDENCE (A3): a
 * photo of the signed form, uploaded through the same write-once path but
 * never gated on the consent it itself documents.
 */
export const SESSION_ARTIFACT_KINDS = [
  'video-part',
  'interpretation-stream',
  'snapshot',
  'snapshot-pose',
] as const;
export type SessionArtifactKind = (typeof SESSION_ARTIFACT_KINDS)[number];

/**
 * Consent bases an artifact can be indexed under (C8/A3; 003 migration
 * vocabulary). Point-in-time provenance, resolved server-side at presign:
 *   'pre-gate-dev-capture'      — indexed before the consent gate existed
 *                                 (spike-faithful dev captures; backfill only).
 *   'session-recording-consent' — the session's creation fact carried
 *                                 recording consent when this was presigned.
 *   'episode-consent-record'    — the artifact IS the consent evidence (A3's
 *                                 signed-document photo); it documents consent
 *                                 rather than being captured under it.
 * Real episode consent (A2/A4) extends this vocabulary; it never rewrites it.
 */
export const CONSENT_BASES = [
  'pre-gate-dev-capture',
  'session-recording-consent',
  'episode-consent-record',
] as const;
export type ConsentBasis = (typeof CONSENT_BASES)[number];

export const presignUploadRequestSchema = z.object({
  sessionId: z.uuid(),
  kind: z.enum(SESSION_ARTIFACT_KINDS),
  /** Monotonic part number within the session. Parts rotate during recording. */
  partNumber: z.number().int().min(1),
  contentType: z.string().min(1),
});
export type PresignUploadRequest = z.infer<typeof presignUploadRequestSchema>;

/** Route body for the session-scoped endpoint (sessionId travels in the path). */
export const presignUploadBodySchema = presignUploadRequestSchema.omit({ sessionId: true });
export type PresignUploadBody = z.infer<typeof presignUploadBodySchema>;

/**
 * Route body for the episode-scoped consent-document presign (A3). The kind is
 * fixed by the route; parts are pages of the signed form (a multi-page form is
 * parts 1..n). JPEG only in V1 — the wizard attaches camera photos.
 */
export const presignConsentDocumentBodySchema = z.object({
  partNumber: z.number().int().min(1),
  contentType: z.literal('image/jpeg'),
});
export type PresignConsentDocumentBody = z.infer<typeof presignConsentDocumentBodySchema>;

export const presignUploadResponseSchema = z.object({
  ok: z.literal(true),
  /** Index row backing this upload — the id the device confirms against. */
  artifactId: z.uuid(),
  uploadUrl: z.url(),
  /** Server-derived. Clients never choose their own storage keys. */
  objectKey: z.string().min(1),
  expiresInSeconds: z.number().int().positive(),
  /**
   * The artifact row's CURRENT lifecycle state at presign time. 'uploaded'
   * means confirmed bytes already sit under this object key — a client about
   * to PUT different bytes is reusing a part number (the RT#5 rejoin
   * overwrite, INC3-VERDICT-2026-07-25 D3) and must refuse rather than
   * replace a stored recording. 'abandoned' means a device reported the
   * part's local bytes gone (§6 d6) — presigning again is legal: the bytes
   * being back is exactly the revival story, and history keeps both events.
   * 'purged' never reaches a client through this response, because the route
   * refuses to presign a purged part at all — there is no restore, that is
   * what purge means (increment-9 plan §4). It is in the enum so that a
   * client's parser can never become the thing that breaks if that refusal is
   * ever reordered.
   * Optional so older clients' parsers accept responses either way.
   */
  uploadState: z.enum(['presigned', 'uploaded', 'abandoned', 'purged']).optional(),
});
export type PresignUploadResponse = z.infer<typeof presignUploadResponseSchema>;

/**
 * POST /api/artifacts/:artifactId/presign-get — the system's READ path
 * (TM-08). Issued only for artifacts with confirmed bytes; every issuance
 * writes an access_log row (this module's header obligation §2, finally
 * discharged). Short TTL: a download URL is consumed immediately.
 */
export const presignGetResponseSchema = z.object({
  ok: z.literal(true),
  artifactId: z.uuid(),
  downloadUrl: z.url(),
  expiresInSeconds: z.number().int().positive(),
});
export type PresignGetResponse = z.infer<typeof presignGetResponseSchema>;

/**
 * Device → server report that a queued upload's local file has VANISHED
 * (dead-letter, increment-4 §6 d6). Appends an `upload-abandoned` event
 * (migration 008 vocabulary) so the artifact index shows the gap durably and
 * the D9-style sweep can attribute it — a gap you can query is a defect, a
 * gap you cannot is a cover-up. Keyed by the artifact natural key because
 * the artifact row may not exist yet: a part can vanish before its first
 * presign ever succeeded (queued offline throughout), and the report itself
 * then creates the identity row the gap hangs on.
 */
export const abandonUploadBodySchema = z.object({
  kind: z.enum(SESSION_ARTIFACT_KINDS),
  partNumber: z.number().int().min(1),
  contentType: z.string().min(1),
  /** Last locally-known size of the vanished file, when the queue had one. */
  sizeBytes: z.number().int().min(0).optional(),
  /** Client event time (device clock) — when the vanish was detected. */
  occurredAt: z.iso.datetime().optional(),
  /** Queue-side evidence (fileType, reason, attempt counts…), stored verbatim. */
  detail: z.record(z.string(), z.unknown()).optional(),
});
export type AbandonUploadBody = z.infer<typeof abandonUploadBodySchema>;

/**
 * Device → server confirmation that the presigned PUT completed. Appends an
 * `upload-confirmed` event to the artifact index (ADR-0010 extension §2);
 * repeats are harmless appended facts, so retries need no special casing.
 */
export const confirmUploadRequestSchema = z.object({
  sizeBytes: z.number().int().min(0),
  /** SHA-256 of the uploaded bytes when the device computed one (ADR-0010 §6). */
  contentHash: z.string().min(1).optional(),
  /** Client event time (device clock) — distinct from server receive time. */
  occurredAt: z.iso.datetime().optional(),
  /** Transport detail (e.g. S3 ETag); stored verbatim in the event detail. */
  detail: z.record(z.string(), z.unknown()).optional(),
});
export type ConfirmUploadRequest = z.infer<typeof confirmUploadRequestSchema>;

/** One artifact-index entry as served by the session artifact listing. */
export const captureArtifactSchema = z.object({
  id: z.uuid(),
  /** Exactly one scope is set (012): session capture vs episode document. */
  sessionId: z.uuid().nullable(),
  episodeId: z.uuid().nullable().optional(),
  kind: z.enum(ARTIFACT_KINDS),
  partNumber: z.number().int().min(1),
  objectKey: z.string().min(1),
  contentType: z.string().min(1),
  retentionClass: z.string().min(1),
  /** What consent authority this artifact was captured under (C8/A3). */
  consentBasis: z.enum(CONSENT_BASES),
  createdAt: z.iso.datetime(),
  /**
   * Latest lifecycle event, resolved at read time (never a mutable column).
   * 'abandoned' = a device reported the local bytes gone before any confirmed
   * upload (§6 d6); a later confirmed upload wins — bytes in the bucket
   * outrank a report that they never arrived.
   *
   * 'purged' = the H4 executor deleted the stored object (increment-9 PR5).
   * It OUTRANKS every other state unconditionally, which is the one place this
   * resolution is not "latest event wins": purge is terminal by construction —
   * the bytes are gone, presigning the part again is refused, and no later
   * event can make the object exist. **The index must never report `uploaded`
   * for media that no longer exists**; after a purge the index is all that is
   * left (no row is deleted — ADR-0010 ext §3), so a record asserting the
   * existence of what it destroyed is the worst thing this surface could say.
   */
  uploadState: z.enum(['presigned', 'uploaded', 'abandoned', 'purged']),
  uploadedSizeBytes: z.number().int().min(0).nullable(),
  uploadConfirmedAt: z.iso.datetime().nullable(),
  /** The device-reported hash from the confirming event (ADR-0010 §6); the
   * study export's artifact index carries it (register H2: "what exists,
   * when, size, content hash, upload state"). Null before confirmation and
   * on confirms that reported none. */
  uploadedContentHash: z.string().nullable(),
});
export type CaptureArtifact = z.infer<typeof captureArtifactSchema>;

/**
 * Storage layout. Record-scoped rather than the spike's user-scoped
 * `recordings/{userName}/...`: the record of truth is the session (capture)
 * or the episode (consent document), and a display name is neither stable nor
 * safe to put in a key path. Exactly one scope per artifact (012).
 */
export function objectKey(
  input: { kind: ArtifactKind; partNumber: number } & (
    { sessionId: string; episodeId?: undefined } | { episodeId: string; sessionId?: undefined }
  ),
): string {
  const part = String(input.partNumber).padStart(3, '0');
  // The named D13 trap: the old video/json two-way split would file snapshot
  // PIXELS as `.json`. Pixels get their pixel extension; only the two sidecar
  // stream kinds are JSON. Consent documents are camera photos of the signed
  // form — JPEG, not JSON (the same trap, one kind later).
  const ext =
    input.kind === 'video-part'
      ? 'mp4'
      : input.kind === 'snapshot'
        ? 'png'
        : input.kind === 'consent-document'
          ? 'jpg'
          : 'json';
  const scope =
    input.sessionId !== undefined ? `sessions/${input.sessionId}` : `episodes/${input.episodeId}`;
  return `${scope}/${input.kind}/part_${part}.${ext}`;
}
